import { useState, useCallback, useEffect } from 'react';
import { highlightsTacticsService, TournamentWithMatches } from '../services/highlightsTacticsService';
import { CaptureItem } from '../types';
import { formatFileSize } from '../utils';

interface UseHighlightsTacticsDataReturn {
  folders: CaptureItem[];
  files: CaptureItem[];
  folderTree: CaptureItem;
  isLoading: boolean;
  tournamentsData: TournamentWithMatches[];
  loadFolderContents: (folderPath?: string, forceReloadTournaments?: boolean) => Promise<void>;
  loadFolderTree: () => Promise<void>;
}

/**
 * Custom hook for managing highlights and tactics data
 * Transforms tournament/match/highlights/tactics structure into CaptureItem format
 */
export const useHighlightsTacticsData = (orgId: string): UseHighlightsTacticsDataReturn => {
  const [folders, setFolders] = useState<CaptureItem[]>([]);
  const [files, setFiles] = useState<CaptureItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [folderTree, setFolderTree] = useState<CaptureItem>({
    id: 'root',
    name: 'All Files',
    type: 'folder',
    children: [],
  });
  const [tournamentsData, setTournamentsData] = useState<TournamentWithMatches[]>([]);

  // Load tournaments with matches
  const loadTournamentsData = useCallback(async () => {
    if (!orgId) return;

    setIsLoading(true);
    try {
      const result = await highlightsTacticsService.getTournamentsWithMatches(orgId);
      if (result.success && result.data) {
        setTournamentsData(result.data);
      } else {
        console.error('Error loading tournaments:', result.error);
        setTournamentsData([]);
      }
    } catch (error) {
      console.error('Error in loadTournamentsData:', error);
      setTournamentsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // Parse folder path: tournamentId/matchId/highlights|tactics
  const parseFolderPath = useCallback((folderPath: string) => {
    const parts = folderPath.split('/').filter(Boolean);
    if (parts.length === 0) return { level: 'root' };
    if (parts.length === 1) return { level: 'tournament', tournamentId: parts[0] };
    if (parts.length === 2) return { level: 'match', tournamentId: parts[0], matchId: parts[1] };
    if (parts.length === 3)
      return {
        level: 'assets',
        tournamentId: parts[0],
        matchId: parts[1],
        assetType: parts[2] as 'highlights' | 'tactics',
      };
    return null;
  }, []);

  // Transform tournament to CaptureItem
  const tournamentToItem = useCallback(
    (tournament: TournamentWithMatches): CaptureItem => ({
      id: `tournament-${tournament.id}`,
      name: tournament.name,
      type: 'folder',
      path: tournament.id,
      children: tournament.matches.map((match) => matchToItem(match, tournament.id)),
    }),
    []
  );

  // Transform match to CaptureItem
  const matchToItem = useCallback((match: any, tournamentId: string): CaptureItem => {
    const hasHighlights = match.highlights && match.highlights.length > 0;
    const hasTactics = match.tactics && match.tactics.length > 0;

    const children: CaptureItem[] = [];
    if (hasHighlights) {
      children.push({
        id: `folder-${match.id}-highlights`,
        name: 'Highlights',
        type: 'folder',
        path: `${tournamentId}/${match.id}/highlights`,
      });
    }
    if (hasTactics) {
      children.push({
        id: `folder-${match.id}-tactics`,
        name: 'Tactics',
        type: 'folder',
        path: `${tournamentId}/${match.id}/tactics`,
      });
    }

    return {
      id: `match-${match.id}`,
      name: match.name,
      type: 'folder',
      path: `${tournamentId}/${match.id}`,
      children,
    };
  }, []);

  // Load folder contents based on path
  const loadFolderContents = useCallback(
    async (folderPath: string = '', forceReloadTournaments: boolean = false) => {
      if (!orgId) return;

      setIsLoading(true);
      try {
        const parsed = parseFolderPath(folderPath);
        
        // Reload tournaments data if requested (e.g., after editing highlights)
        let currentTournamentsData = tournamentsData;
        if (forceReloadTournaments || parsed?.level === 'assets') {
          const result = await highlightsTacticsService.getTournamentsWithMatches(orgId);
          if (result.success && result.data) {
            currentTournamentsData = result.data;
            setTournamentsData(result.data);
          }
        }

        if (!parsed || parsed.level === 'root') {
          // Root level: show tournaments
          const tournamentItems: CaptureItem[] = currentTournamentsData.map((t) => ({
            id: `tournament-${t.id}`,
            name: t.name,
            type: 'folder',
            path: t.id,
          }));
          setFolders(tournamentItems);
          setFiles([]);
          return;
        }

        if (parsed.level === 'tournament') {
          // Tournament level: show matches
          const tournament = currentTournamentsData.find((t) => t.id === parsed.tournamentId);
          if (!tournament) {
            setFolders([]);
            setFiles([]);
            return;
          }

          const matchItems: CaptureItem[] = tournament.matches.map((match) => ({
            id: `match-${match.id}`,
            name: match.name,
            type: 'folder',
            path: `${tournament.id}/${match.id}`,
          }));
          setFolders(matchItems);
          setFiles([]);
          return;
        }

        if (parsed.level === 'match') {
          // Match level: show Highlights and Tactics folders
          const tournament = currentTournamentsData.find((t) => t.id === parsed.tournamentId);
          const match = tournament?.matches.find((m) => m.id === parsed.matchId);
          if (!match) {
            setFolders([]);
            setFiles([]);
            return;
          }

          const folderItems: CaptureItem[] = [];
          if (match.highlights && match.highlights.length > 0) {
            folderItems.push({
              id: `folder-${match.id}-highlights`,
              name: 'Highlights',
              type: 'folder',
              path: `${parsed.tournamentId}/${parsed.matchId}/highlights`,
            });
          }
          if (match.tactics && match.tactics.length > 0) {
            folderItems.push({
              id: `folder-${match.id}-tactics`,
              name: 'Tactics',
              type: 'folder',
              path: `${parsed.tournamentId}/${parsed.matchId}/tactics`,
            });
          }
          setFolders(folderItems);
          setFiles([]);
          return;
        }

        if (parsed.level === 'assets') {
          // Assets level: show highlights or tactics files
          const tournament = currentTournamentsData.find((t) => t.id === parsed.tournamentId);
          const match = tournament?.matches.find((m) => m.id === parsed.matchId);
          if (!match) {
            setFolders([]);
            setFiles([]);
            return;
          }

          if (parsed.assetType === 'highlights') {
            const highlightItems: CaptureItem[] = (match.highlights || []).map((highlight) => ({
              id: `highlight-${highlight.id}`,
              name: highlight.name,
              type: 'file',
              storagePath: highlight.enhanced_storage_path || highlight.output_storage_path || undefined,
              updatedAt: highlight.updated_at,
              status: ['stitched', 'completed'].includes(highlight.status) 
                ? 'ready' 
                : 'processing',
              // Store highlight data for potential future use
              description: highlight.name,
            }));
            setFolders([]);
            setFiles(highlightItems);
            return;
          }

          if (parsed.assetType === 'tactics') {
            const tacticsItems: CaptureItem[] = (match.tactics || []).map((tactic) => ({
              id: `tactics-${tactic.id}`,
              name: tactic.name,
              type: 'file',
              storagePath: tactic.image_storage_path || undefined,
              size: tactic.file_size_bytes ? formatFileSize(tactic.file_size_bytes) : undefined,
              updatedAt: tactic.updated_at,
              status: tactic.status === 'uploaded' ? 'ready' : 'processing',
              description: tactic.name,
            }));
            setFolders([]);
            setFiles(tacticsItems);
            return;
          }
        }

        setFolders([]);
        setFiles([]);
      } catch (error) {
        console.error('Error loading folder contents:', error);
        setFolders([]);
        setFiles([]);
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, tournamentsData, parseFolderPath, orgId]
  );

  // Load folder tree structure
  const loadFolderTree = useCallback(async () => {
    if (!orgId) return;

    setIsLoading(true);
    try {
      const result = await highlightsTacticsService.getTournamentsWithMatches(orgId);
      if (result.success && result.data) {
        // Update tournamentsData state so loadFolderContents can use fresh data
        setTournamentsData(result.data);
        
        const rootChildren: CaptureItem[] = result.data.map((tournament) =>
          tournamentToItem(tournament)
        );

        setFolderTree({
          id: 'root',
          name: 'All Files',
          type: 'folder',
          children: rootChildren,
        });
      }
    } catch (error) {
      console.error('Error loading folder tree:', error);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, tournamentToItem]);

  // Initial load
  useEffect(() => {
    if (orgId) {
      loadTournamentsData();
    }
  }, [orgId, loadTournamentsData]);

  // Load folder contents when tournaments data is ready (for root level)
  useEffect(() => {
    if (tournamentsData.length > 0 && folders.length === 0 && files.length === 0) {
      // Load root level contents when tournaments data is available
      const tournamentItems: CaptureItem[] = tournamentsData.map((t) => ({
        id: `tournament-${t.id}`,
        name: t.name,
        type: 'folder',
        path: t.id,
      }));
      setFolders(tournamentItems);
      setFiles([]);
    }
  }, [tournamentsData, folders.length, files.length]);

  return {
    folders,
    files,
    folderTree,
    isLoading,
    tournamentsData,
    loadFolderContents,
    loadFolderTree,
  };
};

